import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@core/store/hooks';
import { Logo } from '@core/ui';
import { selectIsAuthenticated } from '@features/auth';

/**
 * 404 Not Found page.
 *
 * Two shapes, one component: signed in, it renders as page content inside the
 * app shell (the router mounts it under `AppLayout`, so the header, logout and
 * language switcher stay available); signed out, it is a bare full-height page
 * with the wordmark and the footer, because there is no shell to show.
 */
export function NotFoundPage() {
  const { t } = useTranslation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  // Send logged-out visitors to login (not through the protected redirect).
  const target = isAuthenticated ? '/' : '/login';
  const ctaLabel = isAuthenticated ? t('notFound.backHome') : t('notFound.goToLogin');

  const body = (
    <div className="flex flex-1 flex-col items-center justify-center text-center px-4 py-12">
      {!isAuthenticated && <Logo className="h-8 w-auto mb-8" />}
      <p className="text-5xl font-bold text-[var(--color-accent)]">404</p>
      <h1 className="mt-4 text-xl font-semibold text-[var(--color-text)]">{t('notFound.title')}</h1>
      <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{t('notFound.message')}</p>
      <Link
        to={target}
        className="mt-6 inline-flex items-center min-h-[44px] px-4 py-2.5 text-sm font-medium rounded-lg bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-[var(--color-accent)] transition-colors"
      >
        {ctaLabel}
      </Link>
    </div>
  );

  if (isAuthenticated) {
    return <div className="flex min-h-[50vh] flex-col">{body}</div>;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-bg-subtle)]">
      {body}
      {/* --color-text-secondary, not -muted: this sits on --color-bg-subtle, where
          muted is 4.33:1 and fails AA (see docs/features/ADR-0004-design-system.md). */}
      <footer className="px-4 py-6 text-center text-xs text-[var(--color-text-secondary)]">
        {t('footer.copyright', { year: new Date().getFullYear() })}
      </footer>
    </div>
  );
}
